import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import Editor, { useMonaco } from "@monaco-editor/react";
import { useToast } from "../hooks/useToast";
import { apiRequest } from "../components/lib/queryClient";
import { Plus, Edit, Trash2, Check, Code, Loader2, Eye, X, AlertCircle, Save, Settings, Sparkles, Copy, Download } from "lucide-react";
import { useAuth } from "../components/features/AuthContext";
import { ApiResponse } from "../components/lib/utils";
import { mergeVariableSuggestions } from "../components/lib/indicatorVariables";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";

const FilterApiSchema = z.object({
  id: z.string().optional(),
  function_code: z.string(),
  variables: z.array(z.string()),
  function_name: z.string(),
  category: z.enum(["stocks", "crypto"]),
  type: z.enum(["BUY", "SELL", "EXIT", "LEADER"]),
  isActive: z.boolean(),
});
type FilterApiSchema = z.infer<typeof FilterApiSchema>;

export const extractVariablesFromFunctionCode = (code: string, currentFunctionName: string) => {
  const noStrings = code.replace(/("""[\s\S]*?"""|'''[\s\S]*?'''|(["']))(?:(?!\2)[^\\]|\\.)*?\2/g, "");
  const tokens = noStrings.split(/[^A-Za-z0-9_]+/).filter(token => /^[A-Za-z_][A-Za-z0-9_]*$/.test(token));
  const reserved = new Set([
    "def", "if", "else", "return", "and", "or", "not", "True", "False", "None", "is", "in",
    "abs", "min", "max", "len", "sum", "range", "int", "float", "str", "bool", "list", "dict",
    "set", "tuple", "type", "enumerate", "zip", "map", "filter", "all", "any", "sorted", "reversed",
  ]);
  return [...new Set(tokens.filter(t => !reserved.has(t) && t !== currentFunctionName))];
};


export default function FilterManagement() {
  const [expandedFilterIds, setExpandedFilterIds] = useState<Set<string>>(new Set());
  const [variableSuggestions, setVariableSuggestions] = useState<string[]>([]);
  const [codeInput, setCodeInput] = useState<string>(`def buy_filter(data):
    """
    Buy Filter Function
    Implement your buy logic here
    """
    # Example: Return True for buy conditions
    if data.get('rsi', 0) < 30:
        return True
    return False`);
  const [editingFilter, setEditingFilter] = useState<FilterApiSchema | null>(null);
  const [activeTab, setActiveTab] = useState<"BUY" | "SELL" | "EXIT">("BUY");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [selectedFiltersForCopy, setSelectedFiltersForCopy] = useState<Set<string>>(new Set());
  const editorRef = useRef<any>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, currentTradingMode } = useAuth();
  const category = currentTradingMode;
  const oppositeMode = currentTradingMode === "stocks" ? "crypto" : "stocks";
  const monaco = useMonaco();

  const { data: variableData } = useQuery({
    queryKey: ["filter-variables"],
    queryFn: async () => {
      const response = await apiRequest<ApiResponse<{ variables: string[] }>>(
        "GET",
        "api/get-filter-variables"
      );
      return response.data?.variables || [];
    }
  });

  useEffect(() => {
    if (variableData) {
      // The crypto backend also calculates SMA / RSI / Bollinger variables that
      // common_backend's get-filter-variables does not know about, so they are
      // generated client-side and merged in for crypto filters.
      setVariableSuggestions(
        currentTradingMode === "crypto"
          ? mergeVariableSuggestions(variableData)
          : variableData
      );
    }
  }, [variableData, currentTradingMode]);

  // Monaco autocomplete integration
  useEffect(() => {
    if (!monaco || variableSuggestions.length === 0) return;

    const provider = monaco.languages.registerCompletionItemProvider("python", {
      triggerCharacters: [".", " "],
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const suggestions = variableSuggestions.map((variable) => ({
          label: variable,
          kind: monaco.languages.CompletionItemKind.Variable,
          insertText: variable,
          range,
          detail: "Suggested variable",
          documentation: `Available data variable: ${variable}`,
        }));

        return { suggestions };
      },
    });

    return () => provider.dispose();
  }, [monaco, variableSuggestions]);

  const { data: filters, isLoading } = useQuery<FilterApiSchema[]>({
    queryKey: ["api/filters", currentTradingMode],
    queryFn: async () => {
      const params = new URLSearchParams({ category: currentTradingMode, code: "1" });
      const res = await apiRequest<ApiResponse<FilterApiSchema[]>>("GET", `api/filters?${params}`);
      return res.data;
    },
    enabled: !!currentTradingMode,
  });

  const { data: oppositeFilters, isLoading: isLoadingOpposite } = useQuery<FilterApiSchema[]>({
    queryKey: ["api/filters", oppositeMode],
    queryFn: async () => {
      const params = new URLSearchParams({ category: oppositeMode, code: "1" });
      const res = await apiRequest<ApiResponse<FilterApiSchema[]>>("GET", `api/filters?${params}`);
      return res.data;
    },
    enabled: copyDialogOpen && !!oppositeMode,
  });

  // Create filter
  const { mutate: createFilter, isPending: isCreating } = useMutation({
    mutationFn: async ({ function_code }: { function_code: string }) => {
      const func_name = function_code.match(/def\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/)?.[1] || "unknown_func";
      const variables = extractVariablesFromFunctionCode(function_code, func_name);
      const payload = { function_code, function_name: func_name, variables, category, isActive, type: activeTab };
      return apiRequest("POST", "api/filters", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api/filters"] });
      toast({ title: "Success", description: "Filter created successfully" });
      resetEditor();
    },
    onError: (error: any) => {
      if (error?.type !== "cancelation") {
        toast({ title: "Error", description: error?.message || "Something went wrong", variant: "destructive" });
      }
    }
  });


  const { mutate: copyFilters, isPending: isCopying } = useMutation({
    mutationFn: async (filtersToCopy: FilterApiSchema[]) => {
      const promises = filtersToCopy.map(filter => {
        const payload = {
          function_code: filter.function_code,
          function_name: filter.function_name,
          variables: filter.variables,
          category: currentTradingMode,
          isActive: filter.isActive || true, 
          type: filter.type,
        };
        return apiRequest("POST", "api/filters", payload);
      });
      return Promise.all(promises);
    },
    onSuccess: (_, filtersToCopy) => {
      queryClient.invalidateQueries({ queryKey: ["api/filters"] });
      toast({
        title: "Success",
        description: `Successfully copied ${filtersToCopy.length} filter(s) to ${currentTradingMode}`,
      });
      setCopyDialogOpen(false);
      setSelectedFiltersForCopy(new Set());
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to copy filters",
        variant: "destructive",
      });
    }
  });

  // Update filter
  const { mutate: updateFilter, isPending: isUpdating } = useMutation({
    mutationFn: async ({ id, function_code }: { id: string; function_code: string }) => {
      const func_name = function_code.match(/def\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/)?.[1] || "unknown_func";
      const variables = extractVariablesFromFunctionCode(function_code, func_name);
      const payload = { _id: id, function_code, function_name: func_name, variables, category, isActive, type: activeTab };
      return apiRequest("PUT", "api/filters", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api/filters"] });
      toast({ title: "Success", description: "Filter updated successfully" });
      resetEditor();
    },
    onError: (error: any) => {
      if (error?.type !== "cancelation") {
        toast({ title: "Error", description: error?.message || "Something went wrong", variant: "destructive" });
      }
    }
  });

  // Delete filter
  const { mutate: deleteFilter, isPending: isDeleting } = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `api/filters/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api/filters"] });
      toast({ title: "Success", description: "Filter deleted successfully" });
    },
    onError: (error: any) => {
      if (error?.type !== "cancelation") {
        toast({ title: "Error", description: error?.message || "Something went wrong", variant: "destructive" });
      }
    }
  });

  const resetEditor = () => {
    setCodeInput("");
    setIsActive(true);
    setEditingFilter(null);
  };

  const onSubmit = () => {
    if (!codeInput.trim()) {
      toast({
        title: "Error",
        description: "Filter code cannot be empty",
        variant: "destructive",
      });
      return;
    }

    if (editingFilter?.id) {
      updateFilter({ id: editingFilter.id, function_code: codeInput });
    } else {
      createFilter({ function_code: codeInput });
    }
  };

  const handleEdit = (filter: FilterApiSchema) => {
    setEditingFilter(filter);
    setCodeInput(filter.function_code);
    setIsActive(filter.isActive);
    setActiveTab(filter.type as "BUY" | "SELL" | "EXIT");
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };


  const toggleExpand = (id: string) => {
    setExpandedFilterIds(prev => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const getFilterTypeColor = (type: string) => {
    switch (type) {
      case "BUY": return "from-green-500 to-emerald-600";
      case "SELL": return "from-red-500 to-rose-600";
      case "EXIT": return "from-amber-500 to-orange-600";
      default: return "from-gray-500 to-slate-600";
    }
  };

  const toggleSelectFilterForCopy = (filterId: string) => {
    setSelectedFiltersForCopy(prev => {
      const newSet = new Set(prev);
      newSet.has(filterId) ? newSet.delete(filterId) : newSet.add(filterId);
      return newSet;
    });
  };

  const handleCopyFilters = () => {
    const filtersToCopy = oppositeFilters?.filter(f => selectedFiltersForCopy.has(f.id!)) || [];
    if (filtersToCopy.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one filter to copy",
        variant: "destructive",
      });
      return;
    }
    copyFilters(filtersToCopy);
  };


  const renderFilterItem = (filter: FilterApiSchema) => {
    const isExpanded = expandedFilterIds.has(filter.id!);
    return (
      <div
        key={filter.id}
        className="p-4 border-2 border-slate-200 rounded-xl hover:border-indigo-300 hover:shadow-lg transition-all duration-200 bg-white group"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge
                variant={filter.isActive ? "default" : "secondary"}
                className={filter.isActive ? "bg-green-500" : ""}
              >
                {filter.isActive ? "Active" : "Inactive"}
              </Badge>
              <Badge variant="outline" className="font-medium">
                {filter.type}
              </Badge>
            </div>
            <h4 className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
              {filter.function_name}
            </h4>
            <p className="text-xs text-slate-500 mt-1">
              {filter.variables.length} variables • {filter.category}
            </p>
          </div>
        </div>

        {isExpanded && (
          <div className="mb-3 rounded-lg border border-slate-200 overflow-hidden">
            <div className="bg-slate-900 px-3 py-1.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Code className="h-3 w-3 text-slate-400" />
                <span className="text-xs font-mono text-slate-400">filter.py</span>
              </div>
            </div>
            <div className="bg-slate-50 p-3 max-h-60 overflow-auto">
              <pre className="text-xs font-mono text-slate-800 whitespace-pre-wrap">
                {filter.function_code}
              </pre>
            </div>
          </div>
        )}

        <div className="flex gap-1 pt-3 border-t border-slate-100">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleExpand(filter.id!)}
                  className="flex-1 hover:bg-blue-50 hover:text-blue-600"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isExpanded ? "Hide code" : "View code"}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(filter)}
                  className="flex-1 hover:bg-amber-50 hover:text-amber-600"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit filter</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteFilter(filter.id!)}
                  disabled={isDeleting}
                  className="flex-1 hover:bg-red-50 hover:text-red-600"
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete filter</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    );
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md border-0 shadow-xl">
          <CardContent className="pt-6 text-center">
            <div className="p-4 rounded-2xl bg-slate-100 mb-4 inline-block">
              <AlertCircle className="h-12 w-12 text-slate-400" />
            </div>
            <h3 className="font-semibold text-lg mb-2 text-slate-900">Access Restricted</h3>
            <p className="text-slate-500">Admin privileges required for filter management.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="px-2 mx-auto py-8 space-y-8">
        {/* Header Section */}
        <div className="flex items-center gap-3">
          <div className={`h-10 w-1 bg-gradient-to-b ${getFilterTypeColor(activeTab)} rounded-full`}></div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Trading Filters</h2>
            <p className="text-slate-600">Create and manage your trading filter strategies</p>
          </div>
        </div>

        <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="gap-2 border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
            >
              <Download className="h-4 w-4" />
              Copy from {oppositeMode}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Copy Filters from {oppositeMode}</DialogTitle>
              <DialogDescription>
                Select filters to copy from {oppositeMode} to {currentTradingMode}. Selected filters will be created as new filters.
              </DialogDescription>
            </DialogHeader>

            <ScrollArea className="flex-1 pr-4">
              {isLoadingOpposite ? (
                <div className="space-y-3 py-4">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : oppositeFilters && oppositeFilters.length > 0 ? (
                <div className="space-y-3 py-4">
                  {oppositeFilters.filter(filter => ["BUY", "SELL", "EXIT"].includes(filter.type)).map((filter) => (
                    <div
                      key={filter.id}
                      onClick={() => toggleSelectFilterForCopy(filter.id!)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedFiltersForCopy.has(filter.id!)
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-slate-200 hover:border-slate-300"
                        }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 h-5 w-5 rounded border-2 flex items-center justify-center ${selectedFiltersForCopy.has(filter.id!)
                            ? "bg-indigo-600 border-indigo-600"
                            : "border-slate-300"
                          }`}>
                          {selectedFiltersForCopy.has(filter.id!) && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="font-medium">
                              {filter.type}
                            </Badge>
                            <Badge
                              variant={filter.isActive ? "default" : "secondary"}
                              className={filter.isActive ? "bg-green-500" : ""}
                            >
                              {filter.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <h4 className="font-semibold text-slate-900">
                            {filter.function_name}
                          </h4>
                          <p className="text-xs text-slate-500 mt-1">
                            {filter.variables.length} variables
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="p-4 rounded-2xl bg-slate-100 mb-4">
                    <AlertCircle className="h-10 w-10 text-slate-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-slate-900">No filters found</h3>
                  <p className="text-slate-500 text-sm">
                    There are no filters in {oppositeMode} to copy
                  </p>
                </div>
              )}
            </ScrollArea>

            <DialogFooter className="border-t pt-4">
              <div className="flex items-center justify-between w-full">
                <p className="text-sm text-slate-600">
                  {selectedFiltersForCopy.size} filter(s) selected
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCopyDialogOpen(false)}
                    disabled={isCopying}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCopyFilters}
                    disabled={isCopying || selectedFiltersForCopy.size === 0}
                    className="gap-2"
                  >
                    {isCopying ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Copying...
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Copy {selectedFiltersForCopy.size} Filter(s)
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Filter Type Tabs */}
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {["BUY", "SELL", "EXIT"].map((tab) => {
                const count = filters?.filter(f => f.type === tab).length || 0;
                return (
                  <button
                    key={tab}
                    onClick={() => {
                      setActiveTab(tab as "BUY" | "SELL" | "EXIT");
                      if (!editingFilter) {
                        resetEditor();
                      }
                    }}
                    className={`px-6 py-3 font-semibold whitespace-nowrap rounded-lg transition-all ${activeTab === tab
                        ? `bg-gradient-to-r ${getFilterTypeColor(tab)} text-white shadow-lg scale-105`
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                  >
                    {tab}
                    {count > 0 && (
                      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${activeTab === tab ? "bg-white/20" : "bg-slate-200"
                        }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Filter Editor */}
          <Card ref={formRef} className="xl:col-span-2 border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="border-b bg-gradient-to-r from-indigo-50 to-gray-50">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Sparkles className="h-5 w-5 text-indigo-600" />
                    {editingFilter ? "Edit Filter" : "Create New Filter"}
                  </CardTitle>
                  <CardDescription>
                    Write Python code to define your {activeTab.toLowerCase()} filter logic
                  </CardDescription>
                </div>
                {editingFilter && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200">
                    Editing Mode
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Filter Name</Label>
                  <div className="h-10 px-3 border border-slate-300 rounded-lg bg-slate-50 flex items-center">
                    <span className="text-sm font-medium text-slate-900">
                      {codeInput.match(/def\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/)?.[1] || "Not detected"}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Filter Status</Label>
                  <div className="flex items-center space-x-3 h-10 px-3 border border-slate-300 rounded-lg bg-slate-50">
                    <Switch
                      checked={isActive}
                      onCheckedChange={setIsActive}
                      id="filter-active"
                    />
                    <Label htmlFor="filter-active" className="text-sm font-medium cursor-pointer">
                      {isActive ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <div className="h-2 w-2 rounded-full bg-green-600 animate-pulse"></div>
                          Active
                        </span>
                      ) : (
                        <span className="text-slate-500 flex items-center gap-1">
                          <div className="h-2 w-2 rounded-full bg-slate-400"></div>
                          Inactive
                        </span>
                      )}
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Python Filter Code</Label>
                <div className="rounded-xl border-2 border-slate-200 overflow-hidden shadow-inner">
                  <div className="bg-slate-900 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Code className="h-4 w-4 text-slate-400" />
                      <span className="text-xs font-mono text-slate-400">filter.py</span>
                    </div>
                    <Badge variant="outline" className="text-xs border-slate-700 text-slate-400">
                      Python 3.x
                    </Badge>
                  </div>
                  <Editor
                    height="400px"
                    language="python"
                    theme="vs-dark"
                    value={codeInput}
                    onChange={(value) => setCodeInput(value || "")}
                    onMount={(editor) => (editorRef.current = editor)}
                    options={{
                      minimap: { enabled: false },
                      fontSize: 14,
                      scrollBeyondLastLine: false,
                      wordWrap: "on",
                    }}
                  />
                </div>
              </div>

              <div className="flex justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={resetEditor}
                  disabled={isCreating || isUpdating}
                  className="gap-2"
                >
                  <X className="h-4 w-4" />
                  Clear Editor
                </Button>
                <Button
                  onClick={onSubmit}
                  disabled={isCreating || isUpdating || !codeInput.trim()}
                  className="gap-2 bg-gradient-to-r text-white from-gray-800 to-gray-600 hover:from-gray-700 hover:to-gray-800"
                >
                  {isCreating || isUpdating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {editingFilter ? "Update Filter" : "Create Filter"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Filters List */}
          <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-slate-100">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="h-5 w-5 text-slate-600" />
                {activeTab} Filters
              </CardTitle>
              <CardDescription>
                {filters?.filter(f => f.type === activeTab).length || 0} filter{filters?.filter(f => f.type === activeTab).length !== 1 ? 's' : ''} configured
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : (() => {
                const filteredItems = filters
                  ?.filter(f => f.type === activeTab)
                  .sort((a, b) => a.function_name.localeCompare(b.function_name)) || [];

                if (filteredItems.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center p-12 text-center">
                      <div className="p-4 rounded-2xl bg-slate-100 mb-4">
                        <Code className="h-10 w-10 text-slate-400" />
                      </div>
                      <h3 className="font-semibold text-lg mb-2 text-slate-900">No filters yet</h3>
                      <p className="text-slate-500 text-sm max-w-sm">
                        Create your first {activeTab.toLowerCase()} filter above
                      </p>
                    </div>
                  );
                }

                return (
                  <ScrollArea className="h-[520px] pr-4">
                    <div className="space-y-3">
                      {filteredItems.map(renderFilterItem)}
                    </div>
                  </ScrollArea>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}